import { LoanInfosDto } from 'api/borrower';
import { ReferentialKey } from 'api/referential';
import { useAppSelector } from 'app/hooks';
import { Button } from 'components/ui/Button';
import { GenericCardInfos } from 'components/display/Card/CardInfos';
import { ConfirmationOverlay } from 'components/feedback/ConfirmationOverlay';
import { PenIcon, TrashIcon } from 'components/icons/Icons';
import { NsKey } from 'enums/NsKey';
import { selectReferentialDataByKey } from 'features/referentials/selectors/selectReferentialDataByKey';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { FC, useCallback, useMemo, useState } from 'react';

interface BorrowerLoanCardProps {
  loan: LoanInfosDto;
  onUpdate: (formValues: LoanInfosDto) => void;
  onDelete?: (loanId: string) => void;
}

export const BorrowerLoanCard: FC<BorrowerLoanCardProps> = ({
  loan,
  onUpdate,
  onDelete
}) => {
  const { t } = useDynamicTranslation([NsKey.Borrower]);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const loanTypeReferential = useAppSelector(selectReferentialDataByKey(ReferentialKey.LoanType))
  const loanType = loanTypeReferential.find(item => item.id === loan.loanTypeId)
  const renderBody = useMemo(() => {
    return <>
      <div className="flex flex-col gap-2">
        <div className="flex gap-2 items-center">
          <span className="text-sm font-bold text-zinc-800">
            Prêt {loanType && loanType.label}
          </span>
          <span className="text-sm text-primary font-medium ">
            {loan.loanAmount} €
          </span>
        </div>

        <div className="flex gap-2 items-center">
          <span className="text-sm font-bold text-zinc-800">
            Durée totale
          </span>
          <span className="text-sm text-primary font-medium ">
            {loan.totalDuration} mois
          </span>
        </div>
      </div>
    </>
  }, [loan.totalDuration, loan.loanAmount, loanType]);

  const handleDeleteClick = useCallback(() => {
    setIsDeleteConfirmOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (loan.id) {
      onDelete?.(loan.id);
    }
  }, [onDelete, loan.id]);

  return (
    <>
      <GenericCardInfos
        className="grow"
        headerAction={
          <Button
            tooltip={t('BORROWER_LOAN_FORM.TOOLTIPS.UPDATE_LOAN_GUARANTEES')}
            color="slate"
            round
            onClick={() => onUpdate(loan)}
          >
            <PenIcon className="text-black" />
          </Button>
        }
        footerAction={
          onDelete && (
            <button
              type="button"
              className="p-1 text-gray-400 hover:text-red-500 transition-colors"
              onClick={handleDeleteClick}
            >
              <TrashIcon className="w-5 h-5" />
            </button>
          )
        }
      >
        {renderBody}
      </GenericCardInfos>

      <ConfirmationOverlay
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="COMMON.DELETE_CONFIRMATION_TITLE"
        description="COMMON.DELETE_CONFIRMATION_DESCRIPTION"
        confirmLabel="COMMON.DELETE"
        variant="danger"
      />
    </>
  );
};
